<template>
  <BaseModal
    :title="translations.title"
    size="xl"
    @close="$emit('close')"
  >
    <!-- Machine Info -->
    <div class="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
      <h4 class="font-semibold text-gray-900 mb-2">{{ machine.title }}</h4>
      <p class="text-sm text-gray-600">{{ translations.reference }}: {{ machine.reference_code }}</p>
      <p v-if="machine.brand" class="text-sm text-gray-600">
        {{ translations.brand }}: {{ machine.brand.name }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="submitInquiry" class="space-y-4">
      <!-- Name Fields -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.firstName }} *
          </label>
          <input
            v-model="form.first_name"
            type="text"
            required
            class="input-field"
            :placeholder="translations.firstNamePlaceholder"
            @blur="validator.markFieldTouched('first_name')"
          />
          <div v-if="validator.isFieldTouched('first_name') && !validator.isFieldValid('first_name')" class="text-red-600 text-sm mt-1">
            {{ validator.errors.first_name }}
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.lastName }} *
          </label>
          <input
            v-model="form.last_name"
            type="text"
            required
            class="input-field"
            :placeholder="translations.lastNamePlaceholder"
            @blur="validator.markFieldTouched('last_name')"
          />
          <div v-if="validator.isFieldTouched('last_name') && !validator.isFieldValid('last_name')" class="text-red-600 text-sm mt-1">
            {{ validator.errors.last_name }}
          </div>
        </div>
      </div>

      <!-- Email and Company -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.email }} *
          </label>
          <input
            v-model="form.email"
            type="email"
            required
            class="input-field"
            :placeholder="translations.emailPlaceholder"
            @blur="validator.markFieldTouched('email')"
          />
          <div v-if="validator.isFieldTouched('email') && !validator.isFieldValid('email')" class="text-red-600 text-sm mt-1">
            {{ validator.errors.email }}
          </div>
        </div>

        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">
            {{ translations.company }}
          </label>
          <input
            v-model="form.company"
            type="text"
            class="input-field"
            :placeholder="translations.companyPlaceholder"
            @blur="validator.markFieldTouched('company')"
          />
          <div v-if="validator.isFieldTouched('company') && !validator.isFieldValid('company')" class="text-red-600 text-sm mt-1">
            {{ validator.errors.company }}
          </div>
        </div>
      </div>

      <!-- Phone Field -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ translations.phone }}
        </label>
        <input
          v-model="form.phone"
          type="tel"
          class="input-field"
          :placeholder="translations.phonePlaceholder"
        />
      </div>

      <!-- Description -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          {{ translations.description }} *
        </label>
        <textarea
          v-model="form.message"
          required
          rows="4"
          class="input-field"
          :placeholder="translations.descriptionPlaceholder"
          @blur="validator.markFieldTouched('message')"
        ></textarea>
        <div v-if="validator.isFieldTouched('message') && !validator.isFieldValid('message')" class="text-red-600 text-sm mt-1">
          {{ validator.errors.message }}
        </div>
      </div>

      <!-- Actions -->
      <div class="flex space-x-3 pt-4">
        <button
          type="button"
          @click="$emit('close')"
          class="flex-1 btn-secondary"
        >
          {{ translations.cancel }}
        </button>
        <button
          type="submit"
          :disabled="submitting"
          class="flex-1 btn-primary"
        >
          <span v-if="submitting" class="flex items-center justify-center">
            <svg
              class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                class="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                stroke-width="4"
              ></circle>
              <path
                class="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {{ translations.sending }}
          </span>
          <span v-else>{{ translations.send }}</span>
        </button>
      </div>
    </form>
  </BaseModal>
</template>

<script setup>
import Swal from "sweetalert2";
import { computed, ref } from 'vue';
import BaseModal from "~/components/base/BaseModal.vue";

const props = defineProps({
  machine: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["close"]);

const { $api } = useNuxtApp();
const { t } = useI18n();
const { createFormValidator, commonSchemas } = useFormValidation();

const submitting = ref(false);

const translations = computed(() => {
  try {
    return {
      title: t('machines.inquiry.title'),
      reference: t('machines.inquiry.reference'),
      brand: t('machines.inquiry.brand'),
      firstName: t('machines.inquiry.firstName'),
      lastName: t('machines.inquiry.lastName'),
      firstNamePlaceholder: t('machines.inquiry.firstNamePlaceholder'),
      lastNamePlaceholder: t('machines.inquiry.lastNamePlaceholder'),
      email: t('machines.inquiry.email'),
      emailPlaceholder: t('machines.inquiry.emailPlaceholder'),
      company: t('machines.inquiry.company'),
      companyPlaceholder: t('machines.inquiry.companyPlaceholder'),
      phone: t('machines.inquiry.phone'),
      phonePlaceholder: t('machines.inquiry.phonePlaceholder'),
      description: t('machines.inquiry.description'),
      descriptionPlaceholder: t('machines.inquiry.descriptionPlaceholder'),
      sending: t('machines.inquiry.sending'),
      send: t('machines.inquiry.send'),
      cancel: t('common.cancel'),
      successTitle: t('machines.inquiry.successTitle'),
      successMessage: t('machines.inquiry.successMessage'),
      successButton: t('machines.inquiry.successButton'),
      errorTitle: t('machines.inquiry.errorTitle'),
      errorMessage: t('machines.inquiry.errorMessage'),
      confirm: t('common.confirm')
    };
  } catch (error) {
    return {
      title: 'Machine Inquiry',
      reference: 'Reference',
      brand: 'Brand',
      firstName: 'First Name',
      lastName: 'Last Name',
      firstNamePlaceholder: 'Your first name',
      lastNamePlaceholder: 'Your last name',
      email: 'Email',
      emailPlaceholder: 'your.email@example.com',
      company: 'Company',
      companyPlaceholder: 'Your company',
      phone: 'Phone',
      phonePlaceholder: 'Your phone number',
      description: 'Description',
      descriptionPlaceholder: 'Please describe your inquiry...',
      sending: 'Sending...',
      send: 'Send Inquiry',
      cancel: 'Cancel',
      successTitle: 'Inquiry Sent!',
      successMessage: 'Thank you for your inquiry. We will contact you soon.',
      successButton: 'OK',
      errorTitle: 'Error',
      errorMessage: 'Failed to send inquiry. Please try again.',
      confirm: 'Confirm'
    };
  }
});

const form = ref({
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company: "",
  message: "",
  machine_id: props.machine.id,
});

const validator = createFormValidator({
  first_name: { required: true, minLength: 2 },
  last_name: { required: true, minLength: 2 },
  email: { required: true, email: true },
  phone: { required: false },
  company: { required: false },
  message: { required: true, minLength: 10 },
});

const submitInquiry = async () => {
  if (!validator.validate(form.value)) {
    return;
  }

  submitting.value = true;

      try {
      const requestData = {
        name: `${form.value.first_name} ${form.value.last_name}`,
        email: form.value.email,
        phone: form.value.phone,
        company: form.value.company,
        message: form.value.message,
        machine_id: props.machine.id,
        inquiry_type: "information"
      };

      await $api.post("/contact/inquiry", requestData);

    Swal.fire({
      icon: "success",
      title: translations.value.successTitle,
      text: translations.value.successMessage,
      confirmButtonText: translations.value.successButton,
      confirmButtonColor: "#10B981",
      timer: 3000,
      timerProgressBar: true,
    });

    emit("close");

    form.value = {
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      company: "",
      message: "",
      machine_id: props.machine.id,
    };

    validator.clearErrors();
  } catch (error) {
    Swal.fire({
      icon: "error",
      title: translations.value.errorTitle,
      text: translations.value.errorMessage,
      confirmButtonText: translations.value.confirm,
      confirmButtonColor: "#EF4444",
    });
  } finally {
    submitting.value = false;
  }
};
</script>
